import { useEffect, useRef, useState } from 'react';
import { COLORS } from '../../constants/theme';
import type { NextAge } from '../../types/chancellery';

interface CreateNextAgeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The age this new one follows -- the new age is always currentAgeNumber + 1. */
  currentAgeNumber: number;
  onCreated: (nextAge: NextAge) => void;
}

type StartFromChoice = 'COPY_CURRENT' | 'BASE_DEFAULTS';

/**
 * Create Next Age modal (SF-108). Fully simulated, matching `AddTierModal.tsx`'s decorative-form
 * pattern exactly -- same modal chrome, focus-trap, Escape-to-close behavior, no API calls, no
 * loading/failure states (see spec Key Implementation Note 4 for why). On submit, no network call
 * is made -- completion routes through `UniverseDetail.tsx`'s existing `SuccessDialog` via
 * `onCreated`, not through this modal itself.
 */
export function CreateNextAgeModal({ isOpen, onClose, currentAgeNumber, onCreated }: CreateNextAgeModalProps) {
  const nextAgeNumber = currentAgeNumber + 1;
  const [startFrom, setStartFrom] = useState<StartFromChoice>('COPY_CURRENT');

  const modalRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const radioRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const options: Array<{ value: StartFromChoice; label: string; description: string }> = [
    {
      value: 'COPY_CURRENT',
      label: `Copy Age ${currentAgeNumber}'s overrides`,
      description: `Copies every property currently tuned on Age ${currentAgeNumber} forward to Age ${nextAgeNumber}.`,
    },
    {
      value: 'BASE_DEFAULTS',
      label: 'Start from base defaults',
      description: 'Age gets only hardcoded property defaults -- nothing carried forward.',
    },
  ];

  useEffect(() => {
    if (!isOpen) return;

    triggerRef.current = document.activeElement;
    setStartFrom('COPY_CURRENT');
    radioRefs.current[0]?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'input, button:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleRadioKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, idx: number) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (idx + 1) % options.length;
      setStartFrom(options[next].value);
      radioRefs.current[next]?.focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (idx - 1 + options.length) % options.length;
      setStartFrom(options[prev].value);
      radioRefs.current[prev]?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setStartFrom(options[idx].value);
    }
  };

  const handleCreate = () => {
    // Nulled before onCreated/onClose -- this modal's own trigger (the "+ Create Next Age"
    // button) unmounts in the same render as a successful submit (the CTA card it lives in
    // disappears once the page's resolvedNextAge becomes truthy), so this cleanup effect must not
    // attempt to refocus a by-then-detached element. Nulling makes the cleanup a no-op on this
    // path, leaving SuccessDialog's own focus wiring as the sole owner of where focus lands next.
    triggerRef.current = null;
    onCreated({ ageNumber: nextAgeNumber, startsAtLabel: `when Age ${currentAgeNumber} ends` });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-next-age-title"
        className="rounded-lg shadow-xl max-w-md w-full mx-4"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${COLORS.border}` }}>
          <span id="create-next-age-title" className="text-base font-semibold" style={{ color: COLORS.text.primary }}>
            Create Next Age
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ color: COLORS.text.tertiary, background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}
          >
            ×
          </button>
        </div>

        <div className="p-6">
          <span id="next-age-name-label" className="block mb-1.5 text-sm" style={{ color: COLORS.text.tertiary }}>
            Age name
          </span>
          <span aria-labelledby="next-age-name-label" className="block text-sm font-medium mb-4" style={{ color: COLORS.text.primary }}>
            Age {nextAgeNumber}
          </span>

          <div id="startfrom-label" className="text-sm font-medium mb-2" style={{ color: COLORS.text.tertiary }}>
            Property overrides
          </div>
          <div role="radiogroup" aria-labelledby="startfrom-label" className="flex flex-col gap-2">
            {options.map((option, idx) => {
              const isSelected = option.value === startFrom;
              return (
                <button
                  key={option.value}
                  ref={(el) => { radioRefs.current[idx] = el; }}
                  role="radio"
                  aria-checked={isSelected}
                  tabIndex={isSelected ? 0 : -1}
                  type="button"
                  onClick={() => setStartFrom(option.value)}
                  onKeyDown={(e) => handleRadioKeyDown(e, idx)}
                  className="rounded px-3 py-2 text-sm text-left transition-colors"
                  style={{
                    backgroundColor: isSelected ? COLORS.sectionHeader : COLORS.card,
                    border: `1px solid ${isSelected ? COLORS.link : COLORS.border}`,
                    color: isSelected ? COLORS.text.primary : COLORS.text.secondary,
                    outline: 'none',
                  }}
                >
                  <div className="font-medium">{option.label}</div>
                  <div className="text-xs mt-0.5" style={{ color: COLORS.text.tertiary }}>
                    {option.description}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex gap-3 justify-end px-6 py-4" style={{ borderTop: `1px solid ${COLORS.border}` }}>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded text-sm"
            style={{ backgroundColor: COLORS.cardAlt, color: COLORS.text.secondary, border: `1px solid ${COLORS.border}` }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreate}
            className="px-4 py-2 rounded text-sm font-medium"
            style={{ backgroundColor: COLORS.btnPrimary, color: COLORS.text.primary }}
          >
            Create Next Age
          </button>
        </div>
      </div>
    </div>
  );
}
